/**
 * useAccountId - アカウントID取得フック
 *
 * URLクエリパラメータを優先し、sessionStorageをフォールバックとして使用。
 * Flutter WebView から URL で受け取った accountId を sessionStorage に保存し、
 * React Router 遷移中に URL から accountId が消失しても使用可能にする。
 *
 * 主な用途: 後払い決済時に PUT /api/v1/accounts/:id で email を更新する経路。
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const ACCOUNT_ID_KEY = 'hk_account_id';

/**
 * accountId の形式チェック。
 * URL クエリ由来の値は API パス組み立て（/api/v1/accounts/:id）にそのまま使われるため、
 * 想定外の文字列（パス区切り文字・巨大文字列等）を保存しない。
 * 実際の値は ULID（26桁）だが、厳密一致で正当な値を弾くと email 初期値が入らないバグが
 * 再発するため、英数・ハイフン・アンダースコアの緩いチェックに留める。
 */
const isValidAccountIdFormat = (id: string): boolean => /^[0-9A-Za-z_-]{1,64}$/.test(id);

const accountIdStorage = {
  save: (id: string): void => {
    if (id && isValidAccountIdFormat(id)) {
      sessionStorage.setItem(ACCOUNT_ID_KEY, id);
    }
  },
  get: (): string | undefined => {
    return sessionStorage.getItem(ACCOUNT_ID_KEY) || undefined;
  },
};

/**
 * useAccountIdCapture - accountId の URL → sessionStorage 永続化（App ルートで1回だけ呼ぶ）
 *
 * accountId は WebView エントリ URL（例: /inquiries/:id?accountId=...）にのみ含まれ、
 * React Router の SPA 遷移でクエリが消失する。useAccountId は「呼んだ画面のマウント時」
 * にしか保存しないため、エントリ画面が useAccountId を使っていないと後続画面で取得できない
 * （後払い確認画面で email 初期値が入らないバグの根本原因）。
 * App ルートに本フックを置くことで、どのルートで accountId を受け取っても保存される。
 */
export const useAccountIdCapture = (): void => {
  const [searchParams] = useSearchParams();
  const urlValue = searchParams.get('accountId');

  // urlValue はこのレンダー時点の値がクロージャに固定されるため、子コンポーネントの
  // effect で即座に画面遷移してクエリが消えても、このレンダー分の effect は元の値で
  // 実行され取りこぼさない（レンダリング中の副作用を避けつつ捕捉漏れも防ぐ）。
  useEffect(() => {
    if (urlValue) {
      accountIdStorage.save(urlValue);
    }
  }, [urlValue]);
};

export const useAccountId = (): string | undefined => {
  const [searchParams] = useSearchParams();
  const urlValue = searchParams.get('accountId');
  const [value, setValue] = useState<string | undefined>(
    urlValue || accountIdStorage.get(),
  );

  useEffect(() => {
    if (urlValue) {
      accountIdStorage.save(urlValue);
      setValue(urlValue);
    }
  }, [urlValue]);

  return value;
};
